import type {
  TemplateFormDef,
  TemplateFormFieldId,
  TemplateFormPrefillKey,
  WeatherOptionId,
} from "../constants/templateForms";

export interface TemplateFormPerson {
  rowId: string;
  /** Set when the row was picked from the project's members. */
  userId: number | null;
  name: string;
  company: string;
  certificate: string;
  signer: boolean;
}

export interface TemplateFormAttachment {
  rowId: string;
  name: string;
  /** `upload` — a file staged from the device. `project` — an existing document. */
  source: "upload" | "project";
  documentId?: string;
  /** The project a `project` row was picked from. At building scope the
   *  destination picker defaults to it, so the form files where its
   *  attachments live unless the user deliberately deviates. */
  projectId?: string;
  /** The staged file itself, for `upload` rows — what the backend will store. */
  file?: File;
}

export type TemplateFormValue =
  | string
  | WeatherOptionId[]
  | TemplateFormPerson[]
  | TemplateFormAttachment[];

export type TemplateFormValues = Partial<Record<TemplateFormFieldId, TemplateFormValue>>;

/** What Franklin already knows about the building, org and user. */
export interface TemplateFormPrefill {
  today: string;
  organisation: string;
  buildingName: string;
  buildingAddress: string;
  registryCode: string;
  author: string;
}

// Row ids only have to be unique within one open form, and a counter keeps
// tests deterministic — no crypto/UUID dependency for what is a React key.
let rowCounter = 0;
function nextRowId(prefix: string): string {
  rowCounter += 1;
  return `${prefix}-${rowCounter}`;
}

export function createPerson(overrides: Partial<TemplateFormPerson> = {}): TemplateFormPerson {
  return {
    rowId: nextRowId("person"),
    userId: null,
    name: "",
    company: "",
    certificate: "",
    signer: false,
    ...overrides,
  };
}

export function createAttachment(
  name: string,
  source: TemplateFormAttachment["source"],
  documentId?: string,
  file?: File,
  projectId?: string
): TemplateFormAttachment {
  return { rowId: nextRowId("attachment"), name, source, documentId, projectId, file };
}

/** Fills every field the schema declares, pre-filling the ones we know. */
export function createInitialValues(
  form: TemplateFormDef,
  prefill: TemplateFormPrefill
): TemplateFormValues {
  const values: TemplateFormValues = {};
  for (const section of form.sections) {
    for (const field of section.fields) {
      if (field.type === "people") {
        values[field.id] = [createPerson()];
      } else if (field.type === "attachments") {
        values[field.id] = [];
      } else if (field.type === "weather") {
        values[field.id] = [];
      } else {
        values[field.id] = field.prefill ? prefill[field.prefill] : "";
      }
    }
  }
  return values;
}

/**
 * Prefill keys that describe *this* filing rather than the document's content:
 * when it is being written, and by whom.
 *
 * A form started from an earlier one must not inherit them. The construction
 * diary is a per-day document — copy yesterday's entry and, without this, it
 * files dated yesterday under whoever wrote it, in the statutory "Kande tegija
 * nimi" row. `date` is `required`, so the empty-field check never catches it.
 *
 * Everything else the prefill knows (organisation, building name and address,
 * registry code) is content the user may have deliberately adjusted on the
 * source form, so a copy keeps what it was given.
 */
const PER_FILING_PREFILL: readonly TemplateFormPrefillKey[] = ["today", "author"];

/**
 * Re-applies the per-filing prefill over values copied from another form, so a
 * pre-filled start is dated and attributed to now rather than to its source.
 *
 * Driven off the schema's own `prefill` keys, not a hardcoded field list, so a
 * template that declares `prefill: "today"` on a different field inherits this
 * without anyone remembering to come back here.
 */
export function applyPerFilingPrefill(
  form: TemplateFormDef,
  prefill: TemplateFormPrefill,
  values: TemplateFormValues
): TemplateFormValues {
  const refreshed: TemplateFormValues = { ...values };
  for (const section of form.sections) {
    for (const field of section.fields) {
      if (field.prefill && PER_FILING_PREFILL.includes(field.prefill)) {
        refreshed[field.id] = prefill[field.prefill];
      }
    }
  }
  return refreshed;
}

export function textValue(values: TemplateFormValues, id: TemplateFormFieldId): string {
  const value = values[id];
  return typeof value === "string" ? value : "";
}

function arrayValue<T>(values: TemplateFormValues, id: TemplateFormFieldId): T[] {
  const value = values[id];
  return Array.isArray(value) ? (value as T[]) : [];
}

export function weatherValue(
  values: TemplateFormValues,
  id: TemplateFormFieldId
): WeatherOptionId[] {
  return arrayValue<WeatherOptionId>(values, id);
}

export function peopleValue(values: TemplateFormValues, id: TemplateFormFieldId) {
  return arrayValue<TemplateFormPerson>(values, id);
}

export function attachmentsValue(values: TemplateFormValues, id: TemplateFormFieldId) {
  return arrayValue<TemplateFormAttachment>(values, id);
}

export function toggleWeatherOption(
  selected: WeatherOptionId[],
  option: WeatherOptionId
): WeatherOptionId[] {
  return selected.includes(option)
    ? selected.filter((value) => value !== option)
    : [...selected, option];
}

/** "nr" / "no" follows the document's own language, so an act printed in
 *  English is filed under a name an English reader can read. */
function numberSeparator(locale: "et" | "en"): string {
  return locale === "en" ? "no" : "nr";
}

/** `kaetud-toode-akt` + 3 → `kaetud-toode-akt-nr-3`. */
export function documentNameFor(
  slug: string,
  documentNumber: number,
  locale: "et" | "en" = "et"
): string {
  return `${slug}-${numberSeparator(locale)}-${documentNumber}`;
}

/**
 * The next number for this template in this project: one past the highest
 * `<slug>-nr-<n>` already among the project's documents (extension and any
 * suffix ignored), starting at 1. Matched by prefix rather than a built
 * regular expression — the slug is data, and a dynamic pattern is not.
 *
 * The separator has to be the one `documentNameFor` writes for that locale.
 * Scanning an English slug for `-nr-` matches nothing, which is how the
 * English seed silently under-counted: a project holding five
 * `covered-works-act-no-N` acts opened the sixth showing "No. 1".
 */
export function nextDocumentNumber(
  existingNames: string[],
  slug: string,
  locale: "et" | "en" = "et"
): number {
  const prefix = `${slug}-${numberSeparator(locale)}-`.toLowerCase();
  let highest = 0;
  for (const name of existingNames) {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed.startsWith(prefix)) continue;
    // parseInt stops at the first non-digit, so an extension or suffix is fine.
    const parsed = parseInt(trimmed.slice(prefix.length), 10);
    if (Number.isNaN(parsed)) continue;
    highest = Math.max(highest, parsed);
  }
  return highest + 1;
}
